import { useMemo } from 'react';
import { useFinancialDashboardResource } from './useFinancialDashboardResource';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useExpenseGoals } from './useExpenseGoals';

const WARNING_TOLERANCE = 1.1;

export const goalStatus = {
  ON_TRACK: 'success',
  CLOSE_TO_LIMIT: 'warning',
  OVER_LIMIT: 'error',
};

const toStatus = (actualAmount, targetAmount) => {
  if (actualAmount <= targetAmount) return goalStatus.ON_TRACK;
  if (actualAmount <= targetAmount * WARNING_TOLERANCE) return goalStatus.CLOSE_TO_LIMIT;
  return goalStatus.OVER_LIMIT;
};

export const formatCurrency = (value) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Compara a meta de cada categoria com o que foi gasto no período. Reaproveita
// os endpoints que o dashboard já consome: a receita recebida vem do
// comparativo de entradas e saídas, e o gasto por categoria da pizza de saídas.
export function useGoalsVsActual(period) {
  const { goals, isLoading: isGoalsLoading, error: goalsError, refetch } = useExpenseGoals();
  const { categories, isLoading: isCategoriesLoading } = useExpenseCategories();

  const {
    data: expensesByCategory,
    isLoading: isExpensesLoading,
    error: expensesError,
  } = useFinancialDashboardResource('/financial/dashboard/expenses-by-categories', period, []);

  const {
    data: totals,
    isLoading: isTotalsLoading,
    error: totalsError,
  } = useFinancialDashboardResource(
    '/financial/dashboard/paid-expenses-vs-received-incomes',
    period,
  );

  const totalIncomes = totals?.totalIncomes ?? 0;

  const comparison = useMemo(
    () =>
      goals.map((goal) => {
        const category = categories.find((item) => item.id === goal.categoryId);
        const expense = expensesByCategory.find((item) => item.categoryId === goal.categoryId);

        const actualAmount = expense?.totalAmount ?? 0;
        const targetAmount = (totalIncomes * goal.targetPercentage) / 100;
        const actualPercentage = totalIncomes > 0 ? (actualAmount / totalIncomes) * 100 : 0;

        return {
          categoryId: goal.categoryId,
          categoryName: category?.description ?? expense?.categoryName ?? 'Categoria removida',
          targetPercentage: goal.targetPercentage,
          targetAmount,
          actualAmount,
          actualPercentage,
          status: toStatus(actualAmount, targetAmount),
        };
      }),
    [goals, categories, expensesByCategory, totalIncomes],
  );

  return {
    comparison,
    totalIncomes,
    hasGoals: goals.length > 0,
    isLoading: isGoalsLoading || isCategoriesLoading || isExpensesLoading || isTotalsLoading,
    error: goalsError || expensesError || totalsError,
    refetch,
  };
}
